import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import useU2000AxiosAuthenticated from "../u2000Core/axios/U2000UseAxiosAuthenticated";
import { Outlet } from "react-router-dom";
import i18next from "i18next";
import { useU2000InitLUHandlers } from "../u2000Core/hooks/handler/U2000UseInitLUHandlers";
import { U2000HandleApiError, type U2000ApiErrorResult } from "../u2000Core/axios/U2000HandleApiError";
import { useToast } from "../u2000Core/context/toast/U2000ToastContext";
import { doInitLU, luHandleAppiError, type U3107InitLU } from "./U3107Service";
import { U3107ContextProvider } from "./U3107Context";
import { useU2000LogicalUnitContext } from "../u2000Core/context/logicalUnit/U2000LogicalUnitContext";

export const NS = "u3107";

export const U3107ROLES = {
  READ: "U3107:READ",
  ADD: "U3107:ADD",
  EDIT: "U3107:EDIT",
  DELETE: "U3107:DELETE",
  AUDITS: "U3107:AUDITS",
} as const;
export type U3107ROLES = (typeof U3107ROLES)[keyof typeof U3107ROLES];

const defaultInitLU: U3107InitLU = {
  launchSearch: false,
  hideSearch: true,
  nbRecordPerPage: 50,
  logicalUnitCode: "U3107",
};

const U3107: React.FC = () => {
  const { i18n } = useTranslation();
  const [i18nReady, setI18nReady] = useState<boolean>(false);
  const { t } = useTranslation();
  const toast = useToast();
  const [initLUReady, setInitLUReady] = useState<boolean>(false);
  const u2000AxiosAuthenticated = useU2000AxiosAuthenticated();
  const [initLUData, setInitLUData] = useState<U3107InitLU>(defaultInitLU);

  useEffect(() => {
    i18next.loadNamespaces(["u3107"], (err) => {
      if (err) return console.error("Error loading namespace:", err);
      setI18nReady(true);
    });
  }, [i18n]);

  const handleInitLU = useU2000InitLUHandlers<U3107InitLU>({
    doInitLUFn: async () => {
      return await doInitLU(u2000AxiosAuthenticated);
    },
    onSuccess: (initLU: U3107InitLU) => {
      setInitLUData({
        ...defaultInitLU,
        ...initLU,
        logicalUnitCode: initLU.logicalUnitCode ?? "U3107",
      });
      setInitLUReady(true);
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- aligné U3106 / erreur axios non typée
    onError: ({ error }: { error: any }) => {
      const errorResult = U2000HandleApiError(error, t, NS, luHandleAppiError) as U2000ApiErrorResult;
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
      setInitLUReady(true);
    },
  });

  useEffect(() => {
    handleInitLU.getInitLU();
  }, [t]); // eslint-disable-line react-hooks/exhaustive-deps -- aligné U3106

  const { setLogicalUnitTitle, setLogicalUnitBreadcrumb } = useU2000LogicalUnitContext();
  useEffect(() => {
    if (!i18nReady || !initLUReady) return;
    setLogicalUnitTitle(t("title", { ns: NS }));

    return () => {
      setLogicalUnitTitle(null);
      setLogicalUnitBreadcrumb([]);
    };
  }, [i18nReady, initLUReady, t, setLogicalUnitTitle, setLogicalUnitBreadcrumb]);

  if (!i18nReady || !initLUReady) return null;

  return (
    <U3107ContextProvider initLU={initLUData}>
      <Outlet />
    </U3107ContextProvider>
  );
};

export default U3107;
